import {
  Component,
  ChangeDetectionStrategy,
  inject,
  computed,
  signal,
  input,
  output,
  OnDestroy,
} from '@angular/core';
import { SHARED_IMPORTS } from 'src/app/shared/standalone-imports';
import { RecentUserEntry } from '@app/core/layout/recent-users.service';

/**
 * User Quick Switch Popover Component
 * Gmail-style hover popover for quick user switching.
 * Used with cdkConnectedOverlay in parent component.
 */
@Component({
  selector: 'app-user-quick-switch-popover',
  templateUrl: './user-quick-switch-popover.component.html',
  styleUrls: ['./user-quick-switch-popover.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [...SHARED_IMPORTS],
})
export class UserQuickSwitchPopoverComponent implements OnDestroy {
  /** List of users to display */
  readonly users = input.required<RecentUserEntry[]>();

  /** Current user ID to exclude from list */
  readonly currentUserId = input<string | null>(null);

  /** Emitted when a user is selected */
  readonly userSelected = output<RecentUserEntry>();

  /** Emitted when mouse enters the popover (keep open) */
  readonly mouseEnter = output<void>();

  /** Emitted when mouse leaves the popover (close) */
  readonly mouseLeave = output<void>();

  /** Search query for filtering users */
  readonly searchQuery = signal('');

  /** Filtered users based on search query */
  readonly filteredUsers = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const currentId = this.currentUserId();
    let users = this.users();

    // Exclude current user
    if (currentId) {
      users = users.filter(u => u.id !== currentId);
    }

    // Filter by search query
    if (query) {
      users = users.filter(u => u.name.toLowerCase().includes(query));
    }

    return users;
  });

  private closeTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnDestroy(): void {
    this.clearCloseTimer();
  }

  /** Handle mouse enter - keep popover open */
  onMouseEnter(): void {
    this.clearCloseTimer();
    this.mouseEnter.emit();
  }

  /** Handle mouse leave - close popover after delay */
  onMouseLeave(): void {
    this.closeTimer = setTimeout(() => {
      this.mouseLeave.emit();
    }, 200);
  }

  /** Select a user */
  selectUser(user: RecentUserEntry): void {
    this.userSelected.emit(user);
  }

  /** Update search query */
  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery.set(input.value);
  }

  /** Get initials for avatar */
  getInitials(name: string): string {
    if (!name) return '';
    return name.split(/\s+/).map(n => n[0]).join('').slice(0, 2).toUpperCase();
  }

  private clearCloseTimer(): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }
}
